"use client"

import { useEffect, useState } from "react"
import { Lato } from "next/font/google"
import { AlertTriangle, Home, RefreshCw } from "lucide-react"

import "./globals.css"

import { cn } from "@/lib/utils"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { reportGlobalException } from "@/components/observability/app-insights-global"

// global-error.tsx REPLACES the root layout when it renders, so it must supply
// its own <html>/<body>, re-import globals.css for the design tokens, and re-load
// the fonts. It also sits above the providers — App Insights context is gone here,
// so we report through the module-level handle the provider registered.
const latoFont = Lato({
  subsets: ["latin"],
  weight: ["100", "300", "400", "700", "900"],
  style: ["normal", "italic"],
  variable: "--font-lato",
})

/** Honor the user's stored dark preference — next-themes persists it under "theme". */
function prefersDark(): boolean {
  if (typeof window === "undefined") return false
  try {
    const stored = window.localStorage.getItem("theme")
    if (stored === "dark") return true
    if (stored === "light") return false
    return window.matchMedia("(prefers-color-scheme: dark)").matches
  } catch {
    return false
  }
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [dark, setDark] = useState(false)

  useEffect(() => {
    setDark(prefersDark())
    // Report to Application Insights (no-ops when telemetry is disabled). We never
    // surface error.message to the user — only the digest, as a support reference.
    reportGlobalException(error)
  }, [error])

  return (
    <html
      lang="en"
      dir="ltr"
      className={dark ? "dark" : undefined}
      suppressHydrationWarning
    >
      <body
        className={cn(
          "font-lato",
          "bg-background text-foreground antialiased",
          latoFont.variable
        )}
      >
        <div className="flex min-h-screen items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="mx-auto mb-2 flex size-11 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle
                  className="size-5 text-destructive"
                  aria-hidden
                />
              </div>
              <CardTitle className="text-center text-xl font-semibold">
                Something went wrong
              </CardTitle>
              <CardDescription className="text-center">
                An unexpected error interrupted Agentic Planner. Your data is
                safe — try again, or head back home.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error.digest ? (
                <p className="text-center text-xs text-muted-foreground">
                  Reference{" "}
                  <span className="font-mono tabular-nums">{error.digest}</span>
                </p>
              ) : null}
            </CardContent>
            <CardFooter className="flex justify-center gap-2">
              <Button onClick={reset}>
                <RefreshCw aria-hidden />
                Try again
              </Button>
              <Button variant="outline" asChild>
                {/* Hard navigation on purpose: the root tree (and its providers)
                    crashed, so a full reload to "/" is the reliable recovery —
                    a client-side <Link> would re-enter the broken tree. */}
                {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
                <a href="/">
                  <Home aria-hidden />
                  Go to Home
                </a>
              </Button>
            </CardFooter>
          </Card>
        </div>
      </body>
    </html>
  )
}
